<template>
    <slot v-bind="{
        ...state,
        applyZipCode,
        setLocationContainer,
        setMapContainer,
        setSelectedCategory,
        userPerformances,
        upcomingPerformances,
        formatDistance,
        setSearchQuery,
        setSelectedPerformance,
    }" />
</template>

<script setup>
import { computed, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { debounce, flatten, uniqBy, isEmpty, sortBy, memoize } from 'lodash';
import { format } from 'date-fns';
import Fuse from 'fuse.js';

import {
    mapStyles,
    bowlIcon,
    defaultShuttleIcon,
    activeShuttleIcon,
    defaultParkRideIcon,
    activeParkRideIcon,
    locationIcon,
} from '../utils/map-styles';

import { getUpcomingInstances, getTransportationGroups, getInstanceById } from '../services/inventory';

const API_KEY = 'AIzaSyCgbSWPTdJZqTOT_mndVNYnTLofW3CCY6s';

const BOWL_POSITION = {
    lat: 34.112739,
    lng: -118.338856,
}

const CATEGORY_ICONS = {
    'bowl-shuttle': { default: defaultShuttleIcon, active: activeShuttleIcon },
    'park-ride': { default: defaultParkRideIcon, active: activeParkRideIcon },
}

const MARKER_SIZE = {
    bowl: { w: 70, h: 32 },
    locationDefault: { w: 19, h: 28 },
    locationActive: { w: 22, h: 32 },
    zip: { w: 21, h: 32 },
}

const ZIP_NEARBY_COUNT = 5;

const props = defineProps({
    locationGroups: {
        type: Array,
        default: [],
    },
});

const state = reactive({
    isLoading: true,
    isSingleBooking: false,
    selectedPerformance: null,
    isLoadingPerformances: true,
    selectedCategory: null,
    zipCode: null,
    zipPosition: null,
    locations: [],
    searchQuery: null,
    hoverLocation: null,
    activeLocation: null,
});

const mapReady = ref(false);
const transportationGroups = ref([]);
const bookableInstances = ref([]);

let loadScript;
let locationContainer;
let mapContainer;
let map;
let fuse;
let markers = [];
let activeMarker;

const applyZipCode = event => {
    event.preventDefault();

    state.zipCode = event.target.elements['zip-code'].value.trim();
}

const setLocationContainer = element => {
    locationContainer = element;
}

const setMapContainer = element => {
    mapContainer = element;
}

const setSelectedCategory = category => {
    state.selectedCategory = category;
}

const setSelectedPerformance = performance => {
    state.selectedPerformance = performance;
}

const setSearchQuery = debounce(event => {
    state.searchQuery = event.target.value.trim();
}, 250);

const _geocodeCache = {}

const geocode = query => {
    if (query in _geocodeCache) {
        return _geocodeCache[query];
    }

    _geocodeCache[query] = fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${query}&components=country:US&key=${API_KEY}`).then(response => {
        return response.json();
    }).then(response => {
        if (response.status !== 'ZERO_RESULTS') {
            return response;
        }

        return fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${query}&key=${API_KEY}`).then(response => {
            return response.json();
        });
    });

    return _geocodeCache[query];
}

const haversineDistance = memoize((lat1, lng1, lat2, lng2) => {
    const earthRadiusMiles = 3958.8;

    const latDelta = (lat2 - lat1) * Math.PI / 180;
    const lngDelta = (lng2 - lng1) * Math.PI / 180;

    const h = (
        Math.sin(latDelta / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) *
        Math.cos(lat2 * Math.PI / 180) *
        Math.sin(lngDelta / 2) ** 2
    );

    return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}, (lat1, lng1, lat2, lng2) => `${lat1},${lng1},${lat2},${lng2}`);

const formatDistance = miles => {
    if (miles < 0.1) {
        return `${Math.round(miles * 5280)} ft`;
    }

    return `${Math.round(miles * 10) / 10} mi`;
}

const getBuyUrl = memoize(data => {
    const { category, zoneId, parkingSystemType, parkAndRideId, bowlShuttleId, startDate } = data;

    if (!parkingSystemType) {
        return null;
    }

    if (parkingSystemType === 'ticketmaster') {
        const date = format(startDate, 'yyyyMMdd');
        return `https://www.ticketmaster.com/hollywood-bowl-transportation-tickets-los-angeles/venue/90243?tab=addOns&addOnType=all&daterange=from${date}-to-${date}`;
    }

    if (!zoneId) {
        return null;
    }

    const base = window.AppConfig.blocksOfficeBaseUrl.replace(/^\/|\/$/g, '');

    if (category === 'bowl-shuttle' && bowlShuttleId) {
        return `${base}/booking/parking/bestavailable/${bowlShuttleId}/${zoneId}`;
    }

    if (parkAndRideId) {
        return `${base}/booking/parking/bestavailable/${parkAndRideId}/${zoneId}`;
    }

    return null;
}, data => JSON.stringify(data));

const updateAvailableLocations = () => {
    let groupIds;

    if (state.selectedPerformance) {
        groupIds = [ state.selectedPerformance.transportation_group ];
    } else {
        groupIds = props.locationGroups;
    }

    const groups = transportationGroups.value.filter(group => {
        return groupIds.includes(group.id);
    });

    let locations = groups.map(group => {
        return group.locations;
    });

    locations = flatten(locations);

    locations = uniqBy(locations, 'id');

    locations = locations.map(location => {
        let distanceFromZip = null;
        let buyUrl = null;

        if (state.zipPosition) {
            distanceFromZip = haversineDistance(state.zipPosition.lat, state.zipPosition.lng, parseFloat(location.latitude), parseFloat(location.longitude));
        }

        if (state.selectedPerformance) {
            buyUrl = getBuyUrl({
                category: location.category,
                zoneId: location.zone_id,
                parkingSystemType: state.selectedPerformance.attributes?.custom?.parking_system_type,
                parkAndRideId: state.selectedPerformance.attributes?.custom?.park_and_ride_id,
                bowlShuttleId: state.selectedPerformance.attributes?.custom?.bowl_shuttle_id,
                startDate: state.selectedPerformance.startDate,
            });
        }

        return {
            ...location,
            distanceFromBowl: haversineDistance(BOWL_POSITION.lat, BOWL_POSITION.lng, parseFloat(location.latitude), parseFloat(location.longitude)),
            distanceFromZip,
            buyUrl,
        }
    });

    if (state.selectedCategory) {
        locations = locations.filter(location => {
            return location.category === state.selectedCategory;
        });
    }

    state.locations = sortBy(locations, [
        location => location.distanceFromZip ?? Infinity,
        location => location.distanceFromBowl,
    ]);
}

const updateDistances = async (newValue, oldValue) => {
    if (newValue !== oldValue) {
        state.zipPosition = null;
    }

    if (isEmpty(newValue)) {
        return;
    }

    const response = await geocode(newValue);

    if (response.results?.length) {
        state.zipPosition = response.results[0].geometry.location;
    }
}

const clearMarkers = () => {
    for (const marker of markers) {
        marker.setMap(null);
    }

    markers = [];
    activeMarker = null;
}

const makeIcon = (url, { w, h }) => ({
    url,
    scaledSize: new google.maps.Size(w, h),
});

const updateMap = (newValue, oldValue) => {
    if (!mapReady.value) {
        return;
    }

    clearMarkers();

    const bounds = new google.maps.LatLngBounds();

    const marker = new google.maps.Marker({
        position: BOWL_POSITION,
        map,
        title: 'Hollywood Bowl',
        icon: makeIcon(bowlIcon, MARKER_SIZE.bowl),
    });

    bounds.extend(marker.getPosition());
    markers.push(marker);

    for (const location of state.locations) {
        const icons = CATEGORY_ICONS[location.category];

        const marker = new google.maps.Marker({
            position: {
                lat: parseFloat(location.latitude),
                lng: parseFloat(location.longitude),
            },
            map,
            title: location.display_title ?? location.title,
            icon: makeIcon(icons.default, MARKER_SIZE.locationDefault),
            _loc: location,
        });

        marker.addListener('mouseover', () => {
            if (marker === activeMarker) {
                return;
            }

            marker.setIcon(makeIcon(icons.active, MARKER_SIZE.locationDefault));
            state.hoverLocation = location.id;
        });

        marker.addListener('mouseout', () => {
            if (marker === activeMarker) {
                return;
            }

            marker.setIcon(makeIcon(icons.default, MARKER_SIZE.locationDefault));
            state.hoverLocation = null;
        });

        marker.addListener('click', () => {
            if (activeMarker) {
                const prevIcons = CATEGORY_ICONS[activeMarker._loc.category];
                activeMarker.setIcon(makeIcon(prevIcons.default, MARKER_SIZE.locationDefault));
            }

            activeMarker = marker;
            marker.setIcon(makeIcon(icons.active, MARKER_SIZE.locationActive));
            state.activeLocation = location.id;
            locationContainer?.querySelector('#location-' + location.id)?.scrollIntoView();
        });

        bounds.extend(marker.getPosition());
        markers.push(marker);
    }

    if (state.zipPosition) {
        const nearestBounds = new google.maps.LatLngBounds();

        const zipMarker = new google.maps.Marker({
            position: state.zipPosition,
            map,
            title: state.zipCode,
            icon: makeIcon(locationIcon, MARKER_SIZE.zip),
        });

        nearestBounds.extend(zipMarker.getPosition());
        markers.push(zipMarker);

        for (const marker of markers.filter(marker => marker._loc).slice(0, ZIP_NEARBY_COUNT)) {
            nearestBounds.extend(marker.getPosition());
        }

        map.fitBounds(nearestBounds);
    } else {
        map.fitBounds(bounds);
    }
}

const filteredPerformances = computed(() => {
    if (state.searchQuery && fuse) {
        return fuse.search(state.searchQuery).map(result => {
            return result.item;
        });
    }

    return bookableInstances.value;
});

const userPerformances = computed(() => {
    return filteredPerformances.value.filter(instance => {
        return instance.isAttending;
    });
});

const upcomingPerformances = computed(() => {
    return filteredPerformances.value.filter(instance => {
        return !instance.isAttending;
    });
});

onBeforeMount(() => {
    loadScript = new Promise(resolve => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}`;
        script.onload = resolve;
        document.head.appendChild(script);
    });

    const promises = [
        getTransportationGroups().then(response => {
            transportationGroups.value = response;
        }),
    ];

    const instanceId = new URLSearchParams(window.location.search).get('instance');

    if (instanceId) {
        state.isSingleBooking = true;

        promises.push(
            getInstanceById(instanceId).then(response => {
                state.selectedPerformance = response;
                state.isLoadingPerformances = false;
            })
        );
    }

    Promise.all(promises).finally(() => {
        state.isLoading = false;
    });

    if (!instanceId) {
        getUpcomingInstances().then(response => {
            bookableInstances.value = response.filter(instance => {
                return instance.transportation_group;
            });

            fuse = new Fuse(bookableInstances.value, {
                threshold: 0.30,
                useExtendedSearch: true,
                includeScore: false,
                shouldSort: false,
                keys: [
                    'title',
                    'event.title',
                    'artists.name',
                    'program.title',
                    'program.arrangers.name',
                    'program.composers.name',
                ],
            });
        }).finally(() => {
            state.isLoadingPerformances = false;
        });
    }

    watch(() => [ transportationGroups.value, state.selectedPerformance, state.selectedCategory, state.zipPosition ], updateAvailableLocations);
    watch(() => state.zipCode, updateDistances);
    watch(() => [ state.locations, mapReady.value ], updateMap);
});

onMounted(async () => {
    await loadScript;

    map = new google.maps.Map(mapContainer, {
        zoom: 10,
        scrollWheel: false,
        styles: mapStyles,
    });

    mapReady.value = true;
});

onBeforeUnmount(() => {
    clearMarkers();
});
</script>
